import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { getFormattedDate } from "../../../utils/commonFunctions";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import {
  pointsDetailsSubtitleStyle,
  pointsDetailsTitleStyle
} from "../../../components/styles/commonStyles";

interface UserCreditsDetailsPageProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  merchantId: string;
  establishmentName: string;
}

interface CreditDetail {
  PatronId: number;
  MerchantId: number;
  EstablishmentName: string;
  Amount: string;
  DateCreated: string;
  SourceName: string;
  TotalCount: number;
}

const apiEndpoint: any = process.env.REACT_APP_API_BASE_URL;

const UserCreditsDetailsPage: React.FC<UserCreditsDetailsPageProps> = ({
  open,
  onClose,
  userId,
  merchantId,
  establishmentName
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditDetails, setCreditDetails] = useState<CreditDetail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const fetchCreditDetails = useCallback(() => {
    if (!userId || !merchantId) return;

    setLoading(true);
    setError(null);

    const apiPageNumber = currentPage + 1;
    const url = `${apiEndpoint}/loyaltyprogram/credit/user/${userId}/business/${merchantId}?PageNumber=${apiPageNumber}&PageSize=${pageSize}`;

    axios
      .get(url, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((response) => {
        if (response.data.success) {
          setCreditDetails(response.data.data);
          const total =
            response.data.TotalCount ||
            response.data.totalCount ||
            response.data.total ||
            0;
          setTotalCount(total);
        } else {
          setError("Failed to fetch credit details");
        }
      })
      .catch((error) => {
        console.error("Error fetching credit details:", error);
        setError("Failed to fetch credit details");
      })
      .finally(() => setLoading(false));
  }, [userId, merchantId, currentPage, pageSize]);

  useEffect(() => {
    if (open) {
      fetchCreditDetails();
    }
  }, [open, fetchCreditDetails]);

  const handleClose = () => {
    setCurrentPage(0);
    setPageSize(10);
    setError(null);
    onClose();
  };

  const handleRefresh = () => {
    fetchCreditDetails();
  };

  const handleChangePage = (event: any, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const hasMorePages = (currentPage + 1) * pageSize < totalCount;

  const columns = [
    {
      id: "DateCreated",
      label: "Created At",
      minWidth: 200,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "Amount",
      label: "Amount",
      minWidth: 120,
      align: "left",
      format: (value: any) => {
        if (!value) return "-";
        const amount = parseFloat(value);
        const color = amount < 0 ? "error" : "success.main";
        return (
          <Typography variant="body2" color={color} fontWeight="bold">
            ${amount.toFixed(2)}
          </Typography>
        );
      }
    },
    {
      id: "SourceName",
      label: "Source",
      minWidth: 150,
      align: "left",
      format: (value: any) => value || "-"
    },
    {
      id: "PatronId",
      label: "Patron ID",
      minWidth: 120,
      align: "left",
      format: (value: any) => value || "-"
    },
    {
      id: "MerchantId",
      label: "Merchant ID",
      minWidth: 120,
      align: "left",
      format: (value: any) => value || "-"
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh"
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box>
          <Typography variant="h5" component="div" sx={pointsDetailsTitleStyle}>
            Credit Details
          </Typography>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={pointsDetailsSubtitleStyle}
          >
            {establishmentName}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            size="small"
            title="Refresh"
          >
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <CustomTableWithPagination
          columns={columns}
          data={creditDetails}
          apiStatus={loading ? "fetching" : "idle"}
          payloadSize={pageSize}
          currentPage={currentPage}
          totalCount={totalCount}
          lastEvaluatedKey={hasMorePages ? "hasMore" : null}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          isShowPagination={true}
          noDataFoundMessage="No credit details found."
        />
      </DialogContent>
    </Dialog>
  );
};

export default UserCreditsDetailsPage;
